import { isPublicEvent, publicEvent } from '../../core/publicEvents.ts';
import type { EventKind, LedgerEvent, PublicSnapshot } from '../../core/types.ts';
import { clockTime, relTime, solscanTx, usdSigned } from '../lib/format.ts';
import { Empty, Panel, Pill } from './ui.tsx';

const KIND_STYLE: Record<EventKind, { label: string; dot: string; text?: string }> = {
  boot: { label: 'boot', dot: 'bg-dim' },
  tick: { label: 'tick', dot: 'bg-dim' },
  fee_claim: { label: 'fee', dot: 'bg-sky-400' },
  entry: { label: 'open', dot: 'bg-indigo-400' },
  ladder_add: { label: 'ladder', dot: 'bg-indigo-300' },
  take_profit: { label: 'profit', dot: 'bg-up', text: 'text-up' },
  stop_loss: { label: 'stop', dot: 'bg-down', text: 'text-down' },
  trail_armed: { label: 'breakeven', dot: 'bg-teal-400' },
  funding: { label: 'borrowing', dot: 'bg-cyan-500' },
  burn: { label: 'burn', dot: 'bg-burn', text: 'text-burn' },
  redeploy: { label: 'redeploy', dot: 'bg-emerald-500' },
  risk_block: { label: 'paused', dot: 'bg-amber-400', text: 'text-amber-300' },
  error: { label: 'error', dot: 'bg-red-500', text: 'text-red-400' },
};

function Row({ e, now, logo }: { e: LedgerEvent; now: number; logo: string }) {
  const style = KIND_STYLE[e.kind] ?? KIND_STYLE.tick;

  return (
    <li className="slide-in flex items-start gap-2.5 border-b border-edge/50 px-3 py-2 last:border-b-0">
      <span className={`mt-1.5 size-1.5 shrink-0 rounded-full ${style.dot}`} aria-hidden />
      <img src={logo} alt="" className="mt-0.5 size-4 shrink-0 rounded-full opacity-70" width={16} height={16} />

      <div className="min-w-0 flex-1">
        <p className={`text-[12px] leading-snug ${style.text ?? 'text-muted'}`}>
          {e.message}
          {e.txSig && (
            <a
              href={solscanTx(e.txSig)}
              target="_blank"
              rel="noreferrer"
              className="ml-1.5 text-[10px] text-dim underline decoration-dotted hover:text-white"
            >
              tx ↗
            </a>
          )}
        </p>
        <p className="num mt-0.5 text-[10px] text-dim">
          {clockTime(e.ts)} · {relTime(e.ts, now)} · {style.label}
        </p>
      </div>

      {e.usd !== undefined && Math.abs(e.usd) >= 0.005 && (
        <span
          className={`num shrink-0 text-[12px] font-medium ${
            e.kind === 'burn' ? 'text-burn' : e.usd > 0 ? 'text-up' : 'text-down'
          }`}
        >
          {usdSigned(e.usd)}
        </span>
      )}
    </li>
  );
}

export function EventFeed({ snap, now }: { snap: PublicSnapshot | null; now: number }) {
  const events = (snap?.events ?? []).filter(isPublicEvent).map(publicEvent);

  return (
    <Panel
      title="Live activity"
      subtitle="Every decision the engine makes, from both books, newest first."
      right={snap ? <Pill tone="neutral">{snap.tickCount.toLocaleString()} ticks</Pill> : undefined}
      bodyClassName=""
    >
      {events.length === 0 ? (
        <Empty>Waiting for the first tick.</Empty>
      ) : (
        <ul className="max-h-[560px] overflow-y-auto">
          {events.map((e) => (
            <Row key={e.id} e={e} now={now} logo={snap!.funds[e.fund].logo} />
          ))}
        </ul>
      )}
    </Panel>
  );
}
